/**
 * Marks a script element as an intentional, provider-gated chatbot embed so the guard
 * lets it through. Set by the chatbot components themselves; nothing else should set it.
 *
 * An attribute rather than a JS property, because `next/script` builds its own element
 * and only forwards props — it sets attributes before appending (next/dist/client/script.js),
 * so the guard sees this in time.
 */
export const CHATBOT_ALLOW_ATTR = "data-chatbot-allowed";

/**
 * Vendors whose widgets are owned by the `chatbot_provider` setting. Only the selected
 * provider's component may start one; anything else injecting these is bypassing the setting.
 *
 * Hosts, not GTM container ids. A container id is a per-tenant DB value
 * (`universityAppConfig.gtm_id`, which `GtmFromHost` prefers over the JSON fallback), so it
 * cannot be enumerated from the codebase — but a vendor's own domain is fixed, so matching on
 * it holds whatever container a tenant points at. Keep in step with the providers `PageScript`
 * gates: Tawk (`Tawk.tsx`), Kenyt (`Kenyt.tsx`) and Freshdesk (`Freshdesk.tsx`).
 *
 * The upGrad chatbot is deliberately absent: its embed URL is admin-configurable rather than a
 * fixed vendor domain, so there is no stable host to match on. Freshdesk's host is configurable
 * too, but defaults to a `*.freshchat.com` tenant host, so listing it still catches the common
 * case; a Freshdesk on a custom domain is simply not matched, which is safe either way.
 */
export const CHATBOT_VENDOR_HOSTS = ["tawk.to", "kenyt.ai", "freshchat.com"];

/**
 * Blocks chatbot embeds that bypass the `chatbot_provider` setting.
 *
 * GTM containers can inject a vendor's widget directly, with credentials that exist nowhere in
 * this repo. Such a widget never passes through `PageScript`'s provider gate, so no config value
 * can switch it off — it just appears alongside whichever chatbot is actually selected. This
 * patches the two DOM insertion methods those embed snippets use and drops vendor scripts that no
 * provider component vouched for.
 *
 * Vendor bootstraps are tiny loaders: the real widget arrives as further scripts that the
 * bootstrap injects itself (Tawk's is ~2 KB and pulls in `twk-*.js`; Kenyt's `bot-loader.js` and
 * Freshchat's `widget.js` behave the same way). Those carry no attribute of ours, so an allowed
 * bootstrap must *unlock* its vendor — otherwise the guard strangles the very widget it just let
 * in. Once unlocked, that vendor's later scripts pass, except a repeat of the exact bootstrap
 * URL, which is a second injection of something already running rather than a dependency.
 *
 * Must run before the GTM bootstrap, so it is server-rendered into `<head>` ahead of both the
 * multi-tenant and standalone GTM paths. Only script nodes pointing at a listed vendor are
 * touched; everything else — GTM itself included — is handed to the original method untouched.
 *
 * Known gaps: an embed injected via `document.write` is not caught (the embeds in play use
 * `insertBefore`/`appendChild`); and if GTM fires a tag for the *selected* vendor pointing at a
 * different property, after that vendor is unlocked, it would load a second widget.
 */
export function chatbotGuardInlineScript(): string {
  return `(function(){
var ATTR=${JSON.stringify(CHATBOT_ALLOW_ATTR)};
var VENDORS=${JSON.stringify(CHATBOT_VENDOR_HOSTS)};
var unlocked={};
var ownSrc={};
function vendorOf(host){
  for(var i=0;i<VENDORS.length;i++){
    var v=VENDORS[i];
    if(host===v||host.slice(-(v.length+1))==="."+v)return v;
  }
  return null;
}
function shouldBlock(node){
  try{
    if(!node||node.nodeType!==1||node.tagName!=="SCRIPT")return false;
    var raw=(node.getAttribute&&node.getAttribute("src"))||node.src||"";
    if(!raw)return false;
    var url=new URL(raw,location.href);
    var vendor=vendorOf(url.hostname.toLowerCase());
    if(!vendor)return false;
    if(node.hasAttribute&&node.hasAttribute(ATTR)){
      unlocked[vendor]=true;
      ownSrc[url.href]=true;
      return false;
    }
    if(!unlocked[vendor])return true;
    return !!ownSrc[url.href];
  }catch(e){return false;}
}
function guard(proto,name){
  var original=proto[name];
  if(typeof original!=="function")return;
  proto[name]=function(node){
    if(shouldBlock(node)){
      try{console.warn("[ChatbotGuard] Blocked a chatbot embed injected outside the chatbot provider setting (most likely a GTM tag):",node.getAttribute("src"));}catch(e){}
      return node;
    }
    return original.apply(this,arguments);
  };
}
guard(Node.prototype,"appendChild");
guard(Node.prototype,"insertBefore");
})();`;
}
